import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaSearch, FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaChevronRight, FaPlus, FaCamera } from 'react-icons/fa';
import { communityAPI, imageAPI, userAPI } from '../utils/api';

const TYPE_COLORS = {
  '법회':   { bg: '#c0392b', label: '법회' },
  '명상':   { bg: '#009590', label: '명상' },
  '스터디': { bg: '#7c5cbf', label: '스터디' },
  '봉사':   { bg: '#c8a96e', label: '봉사' },
  '친목':   { bg: '#2e7d5b', label: '친목' },
};

const Community = () => {
  const fileInputRef = useRef(null);
  const [communities, setCommunities] = useState([]);
  const [filteredCommunities, setFilteredCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newCommunity, setNewCommunity] = useState({
    title: '', description: '', type: '법회', region: '서울',
    location: '', meeting_date: '', member_limit: 10, is_online: false, contact_info: '', kakao_link: '', image_url: ''
  });

  const fetchUser = async () => {
    try {
      const res = await userAPI.getProfile();
      setCurrentUser(res.data);
    } catch (e) {
      console.error('사용자 정보 로드 실패');
    }
  };

  const fetchCommunities = async (params = {}) => {
    try {
      setLoading(true);
      const res = await communityAPI.getAll(params);
      const data = res.data.results ?? res.data;
      setCommunities(data);
      setFilteredCommunities(data);
    } catch (e) {
      console.error('모임 목록 로드 실패:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    setIsAuthenticated(!!token);
    if (token) fetchUser();
    fetchCommunities();
  }, []);

  useEffect(() => {
    const params = {};
    if (searchTerm)           params.search = searchTerm;
    if (selectedType !== 'all')   params.type   = selectedType;
    if (selectedRegion !== 'all') params.region  = selectedRegion;
    fetchCommunities(params);
  }, [searchTerm, selectedType, selectedRegion]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewCommunity(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('images', file);
      const res = await imageAPI.upload(formData, 'community');
      const imageUrl = res.data[0]?.url;
      if (imageUrl) {
        setNewCommunity(prev => ({ ...prev, image_url: imageUrl }));
      }
    } catch (e) {
      alert('이미지 업로드에 실패했습니다.');
    } finally {
      setUploading(false);
    }
  };

  const handleCreateCommunity = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const payload = {
        ...newCommunity,
        member_limit: Number(newCommunity.member_limit),
      };
      await communityAPI.create(payload);
      setShowCreateModal(false);
      setNewCommunity({ title: '', description: '', type: '법회', region: '서울', location: '', meeting_date: '', member_limit: 10, is_online: false, contact_info: '', kakao_link: '', image_url: '' });
      fetchCommunities();
    } catch (e) {
      alert('모임 생성에 실패했습니다. 다시 시도해주세요.');
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const memberRatio = (c) => Math.round(((c.member_count ?? c.memberCount ?? 0) / (c.member_limit ?? c.memberLimit ?? 1)) * 100);

  // 매니저 또는 관리자 권한 확인
  const isManager = currentUser && (currentUser.type === 'Manager' || currentUser.type === 'Admin');

  return (
    <div className="cm-wrap">
      <div className="cm-header">
        <Container>
          <div className="cm-header-inner">
            <div className="cm-header-text">
              <h1 className="cm-title">불교 모임</h1>
              <p className="cm-subtitle">함께 수행하고 소통하는 도반을 만나보세요.</p>
            </div>
            {isManager && (
              <button className="cm-btn cm-btn--primary cm-btn--header" onClick={() => setShowCreateModal(true)}>
                <FaPlus className="me-1" /> 모임 개설하기
              </button>
            )}
          </div>

          <div className="cm-filter-bar">
            <div className="cm-search">
              <FaSearch className="cm-search-ico" />
              <input 
                type="text" 
                placeholder="관심 있는 모임을 검색해보세요" 
                className="cm-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="cm-filters">
              <select className="cm-select" value={selectedType} onChange={(e) => setSelectedType(e.target.value)}>
                <option value="all">전체 유형</option>
                {Object.keys(TYPE_COLORS).map(type => <option key={type} value={type}>{type}</option>)}
              </select>
              <select className="cm-select" value={selectedRegion} onChange={(e) => setSelectedRegion(e.target.value)}>
                <option value="all">전체 지역</option>
                {['서울','경기','부산','대구','인천','광주','대전','울산','온라인'].map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
        </Container>
      </div>

      <Container className="cm-body">
        {loading ? (
          <div className="cm-loading">
            <span className="cm-spinner">☸</span>
            <p>모임을 불러오는 중입니다...</p>
          </div>
        ) : filteredCommunities.length > 0 ? (
          <Row className="g-4">
            {filteredCommunities.map((c) => (
              <Col key={c.id} lg={4} md={6}>
                <Link to={`/community/${c.id}`} className="cm-card-link">
                  <div className="cm-card">
                    <div className="cm-card-img-wrap">
                      <img src={c.image_url || '/images/default-community.jpg'} alt={c.title} className="cm-card-img" />
                      <div className="cm-card-overlay" />
                      <span 
                        className="cm-card-type" 
                        style={{ background: TYPE_COLORS[c.type]?.bg || '#009590' }}
                      >
                        {c.type}
                      </span>
                      {c.is_online && <span className="cm-card-online">온라인</span>}
                    </div>

                    <div className="cm-card-body">
                      <h3 className="cm-card-title">{c.title}</h3>
                      <p className="cm-card-desc">
                        {c.description.length > 60 ? `${c.description.substring(0, 60)}...` : c.description}
                      </p>

                      <div className="cm-card-meta">
                        <span><FaMapMarkerAlt className="cm-meta-ico" />{c.is_online ? '온라인' : `${c.region} · ${c.location}`}</span>
                        <span><FaCalendarAlt className="cm-meta-ico" />{c.meeting_date}</span>
                        <span><FaUsers className="cm-meta-ico" />{c.member_count ?? 0}/{c.member_limit}명 참여 중</span>
                      </div>

                      <div className="cm-progress-wrap">
                        <div className="cm-progress-bar" style={{ width: `${memberRatio(c)}%`, background: memberRatio(c) >= 100 ? '#e74c3c' : '#009590' }} />
                        <div className="cm-progress-text">참여율 {memberRatio(c)}%</div>
                      </div>

                      <div className="cm-card-footer">
                        <span className="cm-card-date">{new Date(c.created_at).toLocaleDateString()} 개설</span>
                        <span className="cm-card-more">상세보기 <FaChevronRight /></span>
                      </div>
                    </div>
                  </div>
                </Link>
              </Col>
            ))}
          </Row>
        ) : (
          <div className="cm-empty">
            <p>조건에 맞는 모임이 없습니다.</p>
          </div>
        )}
      </Container>

      {/* 모임 개설 모달 */}
      <Modal show={showCreateModal} onHide={() => !submitting && setShowCreateModal(false)} centered size="lg">
        <Modal.Header closeButton className="cm-modal-header">
          <Modal.Title className="cm-modal-title">새로운 모임 개설</Modal.Title>
        </Modal.Header>
        <Modal.Body className="cm-modal-body">
          <form onSubmit={handleCreateCommunity} className="cm-form">
            <div className="cm-form-group">
              <label className="cm-label">대표 이미지</label>
              <div className="cm-upload-zone" onClick={() => fileInputRef.current.click()}>
                {uploading ? '업로드 중...' : newCommunity.image_url ? <img src={newCommunity.image_url} alt="Preview" className="cm-upload-preview" /> : <><FaCamera /> 사진 올리기</>}
                <input type="file" ref={fileInputRef} onChange={handleImageUpload} hidden accept="image/*" />
              </div>
            </div>

            <div className="cm-form-group">
              <label className="cm-label">모임 제목</label>
              <input className="cm-input" type="text" name="title" value={newCommunity.title} onChange={handleInputChange} placeholder="모임의 이름을 입력해주세요" required />
            </div>

            <div className="cm-form-group">
              <label className="cm-label">모임 설명</label>
              <textarea className="cm-input cm-input--area" name="description" value={newCommunity.description} onChange={handleInputChange} placeholder="모임의 목적과 활동 내용을 상세히 적어주세요" rows="4" required />
            </div>

            <Row className="g-3">
              <Col md={6}>
                <div className="cm-form-group">
                  <label className="cm-label">유형</label>
                  <select className="cm-select cm-select--full" name="type" value={newCommunity.type} onChange={handleInputChange}>
                    {Object.keys(TYPE_COLORS).map(type => <option key={type} value={type}>{type}</option>)}
                  </select>
                </div>
              </Col>
              <Col md={6}>
                <div className="cm-form-group">
                  <label className="cm-label">지역</label>
                  <select className="cm-select cm-select--full" name="region" value={newCommunity.region} onChange={handleInputChange} disabled={newCommunity.is_online}>
                    {['서울','경기','부산','대구','인천','광주','대전','울산','온라인'].map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </Col>
            </Row>

            <label className="cm-check-label">
              <input type="checkbox" name="is_online" checked={newCommunity.is_online} onChange={handleInputChange} className="cm-check" />
              온라인 모임입니다
            </label>

            <div className="cm-form-group">
              <label className="cm-label">모임 장소</label>
              <input className="cm-input" type="text" name="location" value={newCommunity.location} onChange={handleInputChange} placeholder={newCommunity.is_online ? 'Zoom 링크 또는 온라인 모임 방식' : '상세 주소'} required />
            </div>

            <div className="cm-form-group">
              <label className="cm-label">모임 일정</label>
              <input className="cm-input" type="text" name="meeting_date" value={newCommunity.meeting_date} onChange={handleInputChange} placeholder="예: 매주 일요일 오전 10시" required />
            </div>

            <Row className="g-3">
              <Col md={6}>
                <div className="cm-form-group">
                  <label className="cm-label">정원</label>
                  <input className="cm-input" type="number" min="1" name="member_limit" value={newCommunity.member_limit} onChange={handleInputChange} required />
                </div>
              </Col>
              <Col md={6}>
                <div className="cm-form-group">
                  <label className="cm-label">연락처</label>
                  <input className="cm-input" type="text" name="contact_info" value={newCommunity.contact_info} onChange={handleInputChange} placeholder="전화번호 또는 이메일" required />
                </div>
              </Col>
            </Row>

            <div className="cm-form-group">
              <label className="cm-label">카카오 오픈채팅 / 모임 링크 (선택)</label>
              <input className="cm-input" type="url" name="kakao_link" value={newCommunity.kakao_link} onChange={handleInputChange} placeholder="예: https://open.kakao.com/o/..." />
            </div>

            <div className="cm-modal-actions">
              <button type="button" className="cm-btn cm-btn--ghost" onClick={() => setShowCreateModal(false)}>취소</button>
              <button type="submit" className="cm-btn cm-btn--primary" disabled={submitting}>{submitting ? '생성 중…' : '모임 만들기'}</button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <style jsx="true">{`
        .cm-wrap { background: #f8f9fa; min-height: 100vh; padding-bottom: 80px; }
        .cm-header { background: #fff; border-bottom: 1px solid #e4edec; padding: 48px 0 32px; }
        .cm-header-inner { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 32px; }
        .cm-title { font-size: 2.2rem; font-weight: 800; color: #2c3e35; margin-bottom: 8px; letter-spacing: -0.02em; }
        .cm-subtitle { color: #6e8a84; font-size: 1rem; margin: 0; }
        
        .cm-filter-bar { display: flex; gap: 16px; align-items: center; }
        .cm-search { position: relative; flex: 1; }
        .cm-search-ico { position: absolute; left: 16px; top: 50%; transform: translateY(-50%); color: #009590; }
        .cm-search-input { width: 100%; padding: 12px 12px 12px 44px; border: 1.5px solid #e4edec; border-radius: 4px; font-size: 0.92rem; transition: all 0.2s; }
        .cm-search-input:focus { outline: none; border-color: #009590; box-shadow: 0 0 0 4px rgba(0,149,144,0.08); }
        
        .cm-filters { display: flex; gap: 8px; }
        .cm-select { padding: 12px 16px; border: 1.5px solid #e4edec; border-radius: 4px; font-size: 0.9rem; background: #fff; cursor: pointer; color: #4a6660; }
        
        .cm-body { padding-top: 40px; }
        .cm-card-link { text-decoration: none; color: inherit; display: block; }
        .cm-card { background: #fff; border: 1px solid #e4edec; border-radius: 4px; overflow: hidden; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); height: 100%; display: flex; flex-direction: column; }
        .cm-card:hover { transform: translateY(-8px); box-shadow: 0 12px 32px rgba(20,30,25,0.12); border-color: #009590; }
        
        .cm-card-img-wrap { position: relative; width: 100%; aspect-ratio: 16 / 9; overflow: hidden; background: #f0f5f5; }
        .cm-card-img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.6s cubic-bezier(0.33, 1, 0.68, 1); }
        .cm-card:hover .cm-card-img { transform: scale(1.1); }
        .cm-card-overlay { position: absolute; inset: 0; background: linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 50%); }
        .cm-card-type { position: absolute; top: 12px; left: 12px; color: #fff; font-size: 0.72rem; font-weight: 700; padding: 4px 10px; border-radius: 2px; z-index: 2; }
        .cm-card-online { position: absolute; top: 12px; right: 12px; background: #009590; color: #fff; font-size: 0.72rem; font-weight: 700; padding: 4px 10px; border-radius: 2px; z-index: 2; box-shadow: 0 2px 8px rgba(0,149,144,0.3); }
        
        .cm-card-body { padding: 24px; flex: 1; display: flex; flex-direction: column; }
        .cm-card-title { font-size: 1.15rem; font-weight: 700; color: #2c3e35; margin-bottom: 10px; line-height: 1.4; }
        .cm-card-desc { font-size: 0.88rem; color: #6e8a84; line-height: 1.6; margin-bottom: 20px; flex: 1; }
        
        .cm-card-meta { display: flex; flex-direction: column; gap: 6px; margin-bottom: 20px; padding: 12px; background: #f8f9fa; border-radius: 4px; }
        .cm-card-meta span { display: flex; align-items: center; gap: 8px; font-size: 0.82rem; color: #4a6660; }
        .cm-meta-ico { color: #009590; font-size: 0.85rem; }
        
        .cm-progress-wrap { margin-bottom: 20px; }
        .cm-progress-bar { height: 6px; border-radius: 3px; margin-bottom: 6px; transition: width 0.4s; }
        .cm-progress-text { font-size: 0.75rem; color: #6e8a84; text-align: right; font-weight: 600; }
        
        .cm-card-footer { display: flex; justify-content: space-between; align-items: center; padding-top: 16px; border-top: 1px solid #f0f5f5; }
        .cm-card-date { font-size: 0.75rem; color: #b2cece; }
        .cm-card-more { font-size: 0.82rem; color: #009590; font-weight: 700; display: flex; align-items: center; gap: 4px; }
        
        .cm-btn { padding: 12px 24px; border-radius: 3px; font-size: 0.92rem; font-weight: 700; transition: all 0.2s; border: none; cursor: pointer; }
        .cm-btn--primary { background: #009590; color: #fff; }
        .cm-btn--primary:hover:not(:disabled) { background: #007873; transform: translateY(-2px); }
        .cm-btn--header { padding: 6px 12px; font-size: 0.75rem; box-shadow: 0 4px 12px rgba(0,149,144,0.2); }
        
        /* Modal Styles */
        .cm-modal-header { border-bottom: 1px solid #f0f5f5; padding: 20px 24px; }
        .cm-modal-title { font-size: 1.2rem; font-weight: 700; color: #2c3e35; }
        .cm-modal-body { padding: 24px; }
        .cm-upload-zone { border: 2px dashed #e4edec; border-radius: 4px; padding: 20px; text-align: center; cursor: pointer; background: #fcfdfd; color: #6e8a84; font-size: 0.88rem; transition: all 0.2s; min-height: 120px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; }
        .cm-upload-zone:hover { background: #f0f5f5; border-color: #009590; color: #009590; }
        .cm-upload-preview { max-width: 100%; max-height: 200px; border-radius: 4px; }
        .cm-form-group { margin-bottom: 20px; }
        .cm-label { display: block; font-size: 0.82rem; font-weight: 700; color: #2c3e35; margin-bottom: 8px; letter-spacing: -0.01em; }
        .cm-input { width: 100%; padding: 12px 14px; border: 1px solid #e4edec; border-radius: 3px; font-size: 0.92rem; transition: border-color 0.2s; }
        .cm-input:focus { outline: none; border-color: #009590; }
        .cm-input--area { resize: none; line-height: 1.6; }
        .cm-modal-body .cm-select { padding: 12px 14px; border: 1px solid #e4edec; border-radius: 3px; font-size: 0.92rem; background: #fff; cursor: pointer; }
        .cm-select--full { width: 100%; }
        .cm-check-label { display: flex; align-items: center; gap: 8px; font-size: 0.88rem; color: #4a6660; margin-bottom: 20px; cursor: pointer; }
        .cm-modal-actions { display: flex; gap: 12px; margin-top: 32px; }
        .cm-modal-actions .cm-btn { flex: 1; }
        .cm-btn--ghost { background: #f0f5f5; color: #4a6660; }
        .cm-btn--ghost:hover { background: #e4edec; }
        
        .cm-loading, .cm-empty { padding: 80px 0; text-align: center; color: #6e8a84; }
        .cm-spinner { display: inline-block; font-size: 2.5rem; color: #009590; opacity: 0.3; animation: spin 2s linear infinite; margin-bottom: 16px; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        
        @media (max-width: 768px) {
          .cm-header-inner { flex-direction: column; align-items: flex-start; gap: 20px; }
          .cm-filter-bar { flex-direction: column; align-items: stretch; }
          .cm-filters { justify-content: space-between; }
          .cm-select { flex: 1; }
        }
      `}</style>
    </div>
  );
};

export default Community;